import { useMemo, useEffect } from "react";
import * as THREE from "three";

import { MapData, Pt } from "../../utils/apolloRenderer";

/**
 * S2 — HDMap 렌더(z-up). 백엔드 MapData(로컬 미터 폴리라인)를 3D 씬에 그린다.
 * 성능: 폴리라인을 색상/타입별로 하나의 THREE.LineSegments(얇은 기본 라인)로 배치해
 * draw call 을 최소화한다(Dreamview 방식). 팻라인(Line2) 대비 매 프레임 부하가 크게 준다.
 * 차선/정지선/신호 = 라인, 횡단보도/교차로 = 면 채움 + 외곽선. (경계선 타입/색은 백엔드 제공값.)
 */

// LaneBoundaryType.Type → 색/점선 (황색=중앙선/대향분리, 백색=차선, CURB=연석)
const boundaryStyle = (t: number): { color: string; dashed: boolean; opacity: number } => {
    switch (t) {
        case 1: return { color: "#e8c84a", dashed: true, opacity: 0.5 };   // DOTTED_YELLOW
        case 3: return { color: "#e8c84a", dashed: false, opacity: 1 };    // SOLID_YELLOW
        case 5: return { color: "#f2d558", dashed: false, opacity: 1 };    // DOUBLE_YELLOW
        case 2: return { color: "#dbe4f2", dashed: true, opacity: 0.5 };   // DOTTED_WHITE
        case 4: return { color: "#dbe4f2", dashed: false, opacity: 1 };    // SOLID_WHITE
        case 6: return { color: "#828c9c", dashed: false, opacity: 1 };    // CURB(연석)
        default: return { color: "#c2cfe4", dashed: false, opacity: 1 };   // UNKNOWN
    }
};

// 여러 폴리라인 → 단일 LineSegments 지오메트리(선분 쌍으로 펼침). 한 번의 draw call로 그린다.
function buildSegments(polylines: Pt[][], z: number): THREE.BufferGeometry | null {
    const pos: number[] = [];
    for (const line of polylines) {
        if (!line || line.length < 2) continue;
        for (let i = 0; i < line.length - 1; i++) {
            pos.push(line[i][0], line[i][1], z, line[i + 1][0], line[i + 1][1], z);
        }
    }
    if (pos.length === 0) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    return g;
}

// 폐곡선 링을 외곽선용 닫힌 폴리라인으로(마지막→첫 점 연결).
function closeRings(rings: Pt[][]): Pt[][] {
    return (rings || []).filter((r) => r.length >= 2).map((r) => [...r, r[0]]);
}

// 폴리라인을 바닥(z0)에서 height만큼 세운 수직 벽 지오메트리(정지선 등).
function buildWall(pts: Pt[], height: number, z0: number): THREE.BufferGeometry | null {
    if (!pts || pts.length < 2) return null;
    const pos: number[] = [], idx: number[] = [];
    for (let i = 0; i < pts.length; i++) {
        pos.push(pts[i][0], pts[i][1], z0);           // 아래
        pos.push(pts[i][0], pts[i][1], z0 + height);  // 위
    }
    for (let i = 0; i < pts.length - 1; i++) {
        const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2, d = (i + 1) * 2 + 1;
        idx.push(a, c, b, b, c, d);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setIndex(idx);
    return g;
}

// 폴리곤 링 → 면 채움 지오메트리(ShapeGeometry).
function buildFills(rings: Pt[][], keep: (g: THREE.BufferGeometry) => void): THREE.ShapeGeometry[] {
    return (rings || []).filter((r) => r.length >= 3).map((r) => {
        const s = new THREE.Shape();
        s.moveTo(r[0][0], r[0][1]);
        for (let i = 1; i < r.length; i++) s.lineTo(r[i][0], r[i][1]);
        s.closePath();
        const g = new THREE.ShapeGeometry(s);
        keep(g);
        return g;
    });
}

// 점선 LineSegments 는 dash 표현을 위해 lineDistance 속성이 필요하다.
const computeDash = (obj: THREE.LineSegments | null) => { if (obj) obj.computeLineDistances(); };

export default function HdMap({ map, visible = true }: { map: MapData | null; visible?: boolean }) {
    const built = useMemo(() => {
        if (!map) return null;
        const disposables: THREE.BufferGeometry[] = [];
        const keep = (g: THREE.BufferGeometry) => { disposables.push(g); };

        const lanes = (map.lanes || []).filter((l) => l.length >= 2);
        const boundaries = (map.laneBoundaries || [])
            .map((pts, i) => ({ pts, type: (map.laneBoundaryTypes || [])[i] ?? 0 }))
            .filter((b) => b.pts.length >= 2);
        const signals = (map.signals || []).filter((s) => s.length >= 2);
        const stopLines = (map.stopLines || []).filter((s) => s.length >= 2);

        // 경계선: 타입별로 묶어 타입 하나당 LineSegments 하나
        const byType = new Map<number, Pt[][]>();
        for (const b of boundaries) {
            if (!byType.has(b.type)) byType.set(b.type, []);
            byType.get(b.type)!.push(b.pts);
        }
        const boundaryGroups = Array.from(byType.entries())
            .map(([type, polys]) => {
                const geom = buildSegments(polys, 0.1);
                if (geom) keep(geom);
                return { geom, ...boundaryStyle(type) };
            })
            .filter((g) => g.geom) as { geom: THREE.BufferGeometry; color: string; dashed: boolean; opacity: number }[];

        const lanesGeom = buildSegments(lanes, 0.07);
        if (lanesGeom) keep(lanesGeom);
        const signalsGeom = buildSegments(signals, 0.14);
        if (signalsGeom) keep(signalsGeom);

        const stopWalls = stopLines.map((s) => buildWall(s, 0.55, 0.02)).filter((g): g is THREE.BufferGeometry => g != null);
        stopWalls.forEach(keep);

        const junctionFills = buildFills(map.junctions || [], keep);
        const crosswalkFills = buildFills(map.crosswalks || [], keep);
        const junctionOutline = buildSegments(closeRings(map.junctions || []), 0.04);
        if (junctionOutline) keep(junctionOutline);
        const crosswalkOutline = buildSegments(closeRings(map.crosswalks || []), 0.06);
        if (crosswalkOutline) keep(crosswalkOutline);

        return {
            lanesGeom, boundaryGroups, signalsGeom, stopWalls,
            junctionFills, crosswalkFills, junctionOutline, crosswalkOutline,
            hasBoundaries: boundaries.length > 0, disposables,
        };
    }, [map]);

    // 맵 교체/언마운트 시 이전 지오메트리 해제(누수 방지)
    useEffect(() => () => { built?.disposables.forEach((g) => g.dispose()); }, [built]);

    if (!map || !visible || !built) return null;
    const laneColor = built.hasBoundaries ? "#5a6b86" : "#8496b0";

    return (
        <group>
            {/* 교차로(가장 아래): 면 + 외곽선 */}
            {built.junctionFills.map((g, i) => (
                <mesh key={"jf" + i} geometry={g} position={[0, 0, 0.03]}>
                    <meshBasicMaterial color="#5a78a0" transparent opacity={0.1} depthWrite={false} />
                </mesh>
            ))}
            {built.junctionOutline && (
                <lineSegments geometry={built.junctionOutline}>
                    <lineBasicMaterial color="#41506a" />
                </lineSegments>
            )}

            {/* 횡단보도: 면 + 외곽선 */}
            {built.crosswalkFills.map((g, i) => (
                <mesh key={"cf" + i} geometry={g} position={[0, 0, 0.05]}>
                    <meshBasicMaterial color="#b4b4b4" transparent opacity={0.16} depthWrite={false} />
                </mesh>
            ))}
            {built.crosswalkOutline && (
                <lineSegments geometry={built.crosswalkOutline}>
                    <lineBasicMaterial color="#6b7280" />
                </lineSegments>
            )}

            {/* 차선 중앙선(경계선 없으면 주 표시) */}
            {built.lanesGeom && (
                <lineSegments geometry={built.lanesGeom}>
                    <lineBasicMaterial color={laneColor} />
                </lineSegments>
            )}

            {/* 차선 경계선(타입별: 황색=중앙선, 백색=차선, 실선/점선) */}
            {built.boundaryGroups.map((b, i) =>
                b.dashed ? (
                    <lineSegments key={"bnd" + i} geometry={b.geom} ref={computeDash}>
                        <lineDashedMaterial color={b.color} dashSize={0.7} gapSize={0.5} transparent opacity={b.opacity} />
                    </lineSegments>
                ) : (
                    <lineSegments key={"bnd" + i} geometry={b.geom}>
                        <lineBasicMaterial color={b.color} transparent opacity={b.opacity} />
                    </lineSegments>
                )
            )}

            {/* 정지선 — 바닥에서 세운 빨간 벽 */}
            {built.stopWalls.map((g, i) => (
                <mesh key={"stop" + i} geometry={g}>
                    <meshBasicMaterial color="#ff4d4d" transparent opacity={0.6} side={THREE.DoubleSide} depthWrite={false} />
                </mesh>
            ))}

            {/* 신호 경계 */}
            {built.signalsGeom && (
                <lineSegments geometry={built.signalsGeom}>
                    <lineBasicMaterial color="#ffcf4d" />
                </lineSegments>
            )}
        </group>
    );
}
